/**
 * TD - Mobile sticky Add to Cart bar.
 *
 * Delegates to the inline add-to-cart-component in the same section so all existing
 * cart/drawer and variant logic is reused. Shows the bar only while the inline Add
 * to Cart button is scrolled out of view (IntersectionObserver). Hidden on desktop
 * via CSS. Rendered by snippets/td-sticky-atc.liquid.
 *
 * While the bar is visible it claims bottom space from the shared floating-widget
 * service (assets/td-floating-widgets.js), which lifts third-party cookie-consent and
 * accessibility toggles up above the bar so they don't overlap the CTA. The build a
 * bundle cart drawer claims the same space, so both pages place those icons the same
 * way.
 */
(function () {
  if (customElements.get('td-sticky-atc')) return;

  var VARIANT_UPDATE_EVENT = 'variant:update';
  var VISIBLE_CLASS = 'td-sticky-atc--visible';
  var HEIGHT_VAR = '--td-sticky-atc-height';
  var WIDGET_CLAIM_ID = 'td-sticky-atc';

  var SELECTORS = {
    // Inline Add to Cart lives in the vendor buy-buttons block (not editable here),
    // so element/attribute querying on theme markup is the approved exception.
    inlineAtc: 'add-to-cart-component button[name="add"]',
    inlineAtcComponent: 'add-to-cart-component',
    inlineAtcLabel: '.add-to-cart-text__content',
    stickyButton: '[data-td-sticky-atc-button]',
    stickyLabel: '[data-td-sticky-atc-label]',
  };

  class TdStickyAtc extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || document;
      this.stickyButton = this.querySelector(SELECTORS.stickyButton);
      this.stickyLabel = this.querySelector(SELECTORS.stickyLabel);
      this.inlineButton = this.section.querySelector(SELECTORS.inlineAtc);
      this.isVisible = false;
      this.syncFrame = null;

      // Nothing to delegate to — leave the bar hidden.
      if (!this.stickyButton || !this.inlineButton) return;

      this.handleStickyClick = this.handleStickyClick.bind(this);
      this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
      this.queueSync = this.queueSync.bind(this);

      this.stickyButton.addEventListener('click', this.handleStickyClick);
      this.section.addEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate);
      window.addEventListener('resize', this.queueSync);

      var observeTarget =
        this.inlineButton.closest(SELECTORS.inlineAtcComponent) || this.inlineButton;
      this.observer = new IntersectionObserver(
        (entries) => {
          for (var i = 0; i < entries.length; i++) {
            this.setVisible(!entries[i].isIntersecting);
          }
        },
        { threshold: 0 }
      );
      this.observer.observe(observeTarget);

      this.syncState();
    }

    disconnectedCallback() {
      if (this.stickyButton) this.stickyButton.removeEventListener('click', this.handleStickyClick);
      if (this.section) this.section.removeEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate);
      window.removeEventListener('resize', this.queueSync);
      if (this.observer) this.observer.disconnect();
      if (this.syncFrame !== null) cancelAnimationFrame(this.syncFrame);
      // Leave no widgets stranded off-position if the section is torn down.
      if (window.TDFloatingWidgets) window.TDFloatingWidgets.release(WIDGET_CLAIM_ID);
    }

    setVisible(visible) {
      this.isVisible = visible;
      this.classList.toggle(VISIBLE_CLASS, visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      this.syncWidgetClaim();
    }

    syncWidgetClaim() {
      if (window.TDFloatingWidgets) {
        window.TDFloatingWidgets.claim(WIDGET_CLAIM_ID, this.isVisible ? this.barHeight() : 0, {
          element: this,
        });
      }
    }

    // translateY doesn't change measured height, so this is valid while the bar is
    // hidden too. Returns 0 on desktop (bar is display:none), which drops the claim.
    barHeight() {
      var height = Math.ceil(this.getBoundingClientRect().height);
      document.documentElement.style.setProperty(HEIGHT_VAR, height + 'px');
      return height;
    }

    handleStickyClick(event) {
      event.preventDefault();
      // Re-query: the inline button can be replaced when the variant changes.
      var inline = this.section.querySelector(SELECTORS.inlineAtc);
      if (inline && !inline.disabled) inline.click();
    }

    handleVariantUpdate() {
      this.queueSync();
    }

    // Inline markup and viewport-dependent bar height can both change. Coalesce those
    // updates into one frame, then refresh the widget claim using the settled layout.
    queueSync() {
      if (this.syncFrame !== null) cancelAnimationFrame(this.syncFrame);
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.syncState();
        this.syncWidgetClaim();
      });
    }

    syncState() {
      var inline = this.section.querySelector(SELECTORS.inlineAtc);
      if (!inline || !this.stickyButton) return;
      this.inlineButton = inline;
      this.stickyButton.disabled = inline.disabled;
      var label = inline.querySelector(SELECTORS.inlineAtcLabel);
      if (label && this.stickyLabel) this.stickyLabel.textContent = label.textContent.trim();
    }
  }

  customElements.define('td-sticky-atc', TdStickyAtc);
})();
